/**
 * Notification type ids — correspond to `notifications.id` rows in the DB.
 * Same catalog as the members app (members/src/lib/constants/notification-types.ts),
 * with CTA routing adapted to the admin app:
 *  - routes that are "external → admin app" for members are INTERNAL here
 *  - member-facing deep links are EXTERNAL → the members platform
 */
export const NOTIFICATION_TYPE_ID = {
  INVITED_TO_COMMUNITY: 1,
  SERVICE_ADDED: 2,
  PAUSED_BY_CM: 3,
  REMOVED_FROM_COMMUNITY: 4,
  INVITATION_DECLINED: 5,
  NEW_PRICELIST_OFFERED: 6,
  NEW_MEMBER_WANTS_TO_JOIN: 7,
  SUPPLIER_LEFT_COMMUNITY: 8,
  SUPPLIER_OFFERING_TO_JOIN: 9,
  TOS_UPDATE: 10,
  NEW_UPDATE_FOR_SUPPLIER: 12,
  JOB_DUE_TOMORROW: 13,
  NEW_JOB_OPPORTUNITY: 14,
  JOB_CANCELED_BY_MEMBER: 15,
  JOB_COMPLETED_AND_PAID: 16,
  NEW_UPDATE_FOR_CM: 17,
  MEMBER_CANT_FIND_SERVICE: 18,
  MEMBER_CANT_FIND_SUPPLIER: 19,
  SUPPLIER_REMOVED_PRICELIST: 20,
  NEW_UPDATE_FOR_MEMBER: 23,
  SUPPLIER_FOUND_FOR_ORDER: 24,
  ORDER_CANCELED_BY_SUPPLIER: 25,
  ONLINE_LINK_ADDED: 26,
  NO_SUPPLIER_FOUND: 27,
  PAYMENT_REQUESTED: 28,
  NEW_SERVICES_AVAILABLE: 29,
  PRICELIST_ADDED: 30,
  VARIANT_ADDED: 31,
  OFFER_APPROVED: 32,
  // Products & Restaurants (COMY1-367)
  CATALOG_ORDER_PLACED_SUPPLIER: 35,
  CATALOG_ORDER_DIGITAL_ITEMS_TO_SEND: 38,
  CATALOG_ORDER_REAPPROVAL_APPROVED: 42,
  CATALOG_ORDER_REAPPROVAL_DECLINED: 43,
  CATALOG_ORDER_CANCELED_BY_MEMBER: 49,
  CATALOG_ORDER_ADDRESS_CHANGED: 59,
  SUPPLIER_ADDED_ITEMS: 58,
  SUPPLIER_REMOVED_STORE_FROM_COMMUNITY: 61,
  CATALOG_ORDER_PLACED_MEMBER: 36,
  CATALOG_ORDER_HAS_DIGITAL_ITEMS: 37,
  CATALOG_ORDER_CONFIRMED: 39,
  CATALOG_ORDER_DECLINED: 40,
  CATALOG_ORDER_ITEMS_UNAVAILABLE: 41,
  CATALOG_ORDER_PREPARING: 44,
  CATALOG_ORDER_READY_FOR_PICKUP: 45,
  CATALOG_ORDER_OUT_FOR_DELIVERY: 46,
  CATALOG_ORDER_COMPLETED: 47,
  CATALOG_ORDER_CANCELED_BY_SUPPLIER: 48,
};

/**
 * ctaAction values:
 *  - "navigate":     go to ctaRoute (internal react-router route, or the members
 *                    platform when isMembersApp is true)
 *  - "contact_info": render CM/supplier contact icons (whatsapp / phone / email)
 *  - "copy":         copy display_params.url (or param3) to the clipboard
 *  - "dynamic":      destination comes from display_params.cta_url (manual sends)
 *  - "tos":          open the in-app TOS dialog (the admin app has no /tos route)
 *  - "none":         no CTA button
 */
export const NOTIFICATION_TYPE_META = {
  [NOTIFICATION_TYPE_ID.INVITED_TO_COMMUNITY]: {
    ctaAction: "navigate",
    ctaRoute: "/find-communities",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.SERVICE_ADDED]: {
    ctaAction: "navigate",
    ctaRoute: "/manage-pricelists",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.PAUSED_BY_CM]: {
    ctaAction: "contact_info",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.REMOVED_FROM_COMMUNITY]: {
    ctaAction: "contact_info",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.INVITATION_DECLINED]: {
    ctaAction: "contact_info",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.NEW_PRICELIST_OFFERED]: {
    ctaAction: "navigate",
    ctaRoute: "/my-suppliers?tab=2",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.NEW_MEMBER_WANTS_TO_JOIN]: {
    ctaAction: "navigate",
    ctaRoute: "/my-members?tab=2",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.SUPPLIER_LEFT_COMMUNITY]: {
    ctaAction: "contact_info",
    hasSecondaryCta: true,
    secondaryCtaAction: "navigate",
    secondaryCtaRoute: "/find-suppliers",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.SUPPLIER_OFFERING_TO_JOIN]: {
    ctaAction: "navigate",
    ctaRoute: "/my-suppliers?tab=2",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.TOS_UPDATE]: {
    ctaAction: "tos",
    receiverRole: "all",
  },
  [NOTIFICATION_TYPE_ID.NEW_UPDATE_FOR_SUPPLIER]: {
    ctaAction: "dynamic",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.JOB_DUE_TOMORROW]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.NEW_JOB_OPPORTUNITY]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.JOB_CANCELED_BY_MEMBER]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.JOB_COMPLETED_AND_PAID]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.NEW_UPDATE_FOR_CM]: {
    ctaAction: "dynamic",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.MEMBER_CANT_FIND_SERVICE]: {
    ctaAction: "navigate",
    ctaRoute: "/find-suppliers",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.MEMBER_CANT_FIND_SUPPLIER]: {
    ctaAction: "navigate",
    ctaRoute: "/find-suppliers",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.SUPPLIER_REMOVED_PRICELIST]: {
    ctaAction: "navigate",
    ctaRoute: "/find-suppliers",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.NEW_UPDATE_FOR_MEMBER]: {
    ctaAction: "dynamic",
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.SUPPLIER_FOUND_FOR_ORDER]: {
    ctaAction: "contact_info",
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.ORDER_CANCELED_BY_SUPPLIER]: {
    ctaAction: "contact_info",
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.ONLINE_LINK_ADDED]: {
    ctaAction: "copy",
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.NO_SUPPLIER_FOUND]: {
    ctaAction: "none",
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.PAYMENT_REQUESTED]: {
    // Member-facing deep link into the members-app order modal payment step —
    // query params are appended in buildCtaRoute from display_params.
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.NEW_SERVICES_AVAILABLE]: {
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.PRICELIST_ADDED]: {
    ctaAction: "navigate",
    ctaRoute: "/manage-pricelists",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.VARIANT_ADDED]: {
    ctaAction: "navigate",
    ctaRoute: "/manage-pricelists",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.OFFER_APPROVED]: {
    ctaAction: "none",
    receiverRole: "supplier",
  },
  // ── Products & Restaurants (COMY1-367) ─────────────────────────────
  // Supplier-side: all of these open one order on My Jobs.
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_PLACED_SUPPLIER]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_DIGITAL_ITEMS_TO_SEND]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_REAPPROVAL_APPROVED]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_REAPPROVAL_DECLINED]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_CANCELED_BY_MEMBER]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_ADDRESS_CHANGED]: {
    ctaAction: "navigate",
    ctaRoute: "/my-jobs",
    receiverRole: "supplier",
  },
  // CM-side: the supplier index.
  [NOTIFICATION_TYPE_ID.SUPPLIER_ADDED_ITEMS]: {
    ctaAction: "navigate",
    ctaRoute: "/my-suppliers",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.SUPPLIER_REMOVED_STORE_FROM_COMMUNITY]: {
    ctaAction: "navigate",
    ctaRoute: "/my-suppliers",
    receiverRole: "cm",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_CONFIRMED]: {
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_DECLINED]: {
    ctaAction: "navigate",
    ctaRoute: "/marketplace/stores",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_CANCELED_BY_SUPPLIER]: {
    ctaAction: "contact_info",
    receiverRole: "member",
  },
    [NOTIFICATION_TYPE_ID.CATALOG_ORDER_PLACED_MEMBER]: {
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_HAS_DIGITAL_ITEMS]: {
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_ITEMS_UNAVAILABLE]: {
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_PREPARING]: {
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_READY_FOR_PICKUP]: {
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_OUT_FOR_DELIVERY]: {
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
  [NOTIFICATION_TYPE_ID.CATALOG_ORDER_COMPLETED]: {
    ctaAction: "navigate",
    ctaRoute: "/",
    isMembersApp: true,
    receiverRole: "member",
  },
};
